import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { PersistColumnWidthsDirective } from '../../shared/directives/persist-column-widths.directive';
import { Select } from 'primeng/select';
import { Tooltip } from 'primeng/tooltip';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { MultiSelect } from 'primeng/multiselect';
import { ChartModule } from 'primeng/chart';
import { Dialog } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { catchError, of } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ProjectUtilizationReportService } from '../../services/project-utilization-report.service';
import { YearMonthFilterService } from '../../services/year-month-filter.service';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import {
  ProjectUtilizationReport,
  ProjectUtilizationProject
} from '../../models/project-utilization-report.model';
import { autoFitColumns, addAutoFilter } from '../../utils/excel-export.util';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

interface DropdownOption {
  label: string;
  value: number;
}

/** One project's period totals, as the summary dialog lists them. */
interface SummaryRow {
  projectId: string;
  name: string;
  allocatedHours: number;
  /** Hours actually logged — "utilized" in the summary's wording. */
  loggedHours: number;
  utilizationPercentage: number | null;
}

@Component({
  selector: 'app-project-utilization-report',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    PersistColumnWidthsDirective,
    Select,
    Tooltip,
    ToggleSwitch,
    MultiSelect,
    ChartModule,
    Dialog,
    ButtonModule
  ],
  templateUrl: './project-utilization-report.component.html',
  styleUrl: './project-utilization-report.component.scss'
})
export class ProjectUtilizationReportComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  private reportService = inject(ProjectUtilizationReportService);
  private yearMonthFilter = inject(YearMonthFilterService);
  private messageService = inject(MessageService);
  readonly pageHeaderService = inject(PageHeaderSearchActionService);

  report: ProjectUtilizationReport | null = null;
  loading = false;

  startYear: number;
  startMonth: number;
  endYear: number;
  endMonth: number;

  yearOptions: DropdownOption[] = [];
  monthOptions: DropdownOption[] = [];

  /**
   * Draft work logs count towards actuals by default so the report is usable before the
   * period has closed.
   */
  includeDrafts = true;

  searchText = '';
  expandedRows: Record<string, boolean> = {};

  // Filters mirror the Monthly Allocation grid's, so the same question narrows the same way
  // on both screens. Options are derived from the report rather than from the full reference
  // lists, so a dropdown never offers a value with nothing behind it.
  selectedPMs: string[] = [];
  selectedProjectIds: string[] = [];
  projectTypeFilter: 'all' | 'main' | 'presales' = 'all';
  selectedInvoiceTypes: string[] = [];
  selectedCostCenterIds: string[] = [];
  selectedServiceGroupIds: string[] = [];

  readonly projectTypeOptions = [
    { label: 'All projects', value: 'all' },
    { label: 'Main delivery', value: 'main' },
    { label: 'Presales', value: 'presales' }
  ];

  /** Table by default: the chart ranks and compares, the table is what people read rows off. */
  viewMode: 'table' | 'chart' = 'table';

  /**
   * How many projects the bar chart draws, largest first. 0 means all.
   *
   * Effort is heavily skewed — in production 73 of 104 projects are under 1% of the total
   * each — so drawing everything buries the ones that matter in a field of slivers. The
   * default shows the handful that carry most of the effort, and "All" is one click away for
   * when you want the whole picture.
   */
  chartTopN = 20;

  readonly chartTopNOptions = [
    { label: 'Top 10', value: 10 },
    { label: 'Top 20', value: 20 },
    { label: 'Top 40', value: 40 },
    { label: 'All', value: 0 }
  ];


  chartData: unknown = { labels: [], datasets: [] };
  chartOptions: unknown = {};

  /** Redraws the chart when the light/dark theme flips, since Chart.js needs real colours. */
  private themeObserver?: MutationObserver;

  /** Longest span the API will build; mirrored here so the message appears before the call. */
  private readonly maxMonths = 12;

  constructor() {
    // Opens on the current month at both ends — a one-month range, so the page shows
    // something immediately rather than an empty span the reader has to configure.
    this.startYear = this.yearMonthFilter.currentYear;
    this.startMonth = this.yearMonthFilter.currentMonth;
    this.endYear = this.yearMonthFilter.currentYear;
    this.endMonth = this.yearMonthFilter.currentMonth;
  }

  ngOnInit(): void {
    this.yearOptions = this.yearMonthFilter.getYearOptions();
    this.monthOptions = this.yearMonthFilter.getMonthOptions();

    // Search, filter toggle and Export are driven from the shared main header, as on the
    // other report pages, so this page has no controls of its own competing with it.
    this.pageHeaderService.clearPageConfig();
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search project or member...');
    this.pageHeaderService.setCanShowExport(true);
    this.pageHeaderService.setExportDisabled(false);

    this.pageHeaderService.searchText
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(text => {
        this.searchText = text ?? '';
        // The chart draws the filtered set, so it follows the search like the table does.
        if (this.viewMode === 'chart') {
          this.buildChart();
        }
      });

    this.pageHeaderService.exportClick
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.exportToExcel());

    // Chart.js paints a canvas with literal colours, so a theme flip has to redraw it —
    // unlike the DOM, it cannot re-resolve CSS variables on its own.
    this.themeObserver = new MutationObserver(() => {
      if (this.viewMode === 'chart') {
        this.buildChart();
      }
    });
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    this.loadReport();
  }

  ngOnDestroy(): void {
    // Reset so the header's search/export/toggle do not linger on the next page.
    this.pageHeaderService.clearPageConfig();
    this.themeObserver?.disconnect();
  }

  loadReport(): void {
    if (this.rangeError) {
      // Nothing to fetch: the inline message already says what to fix.
      this.report = null;
      return;
    }

    this.loading = true;
    this.report = null;

    this.reportService
      .getReport(this.startYear, this.startMonth, this.endYear, this.endMonth, this.includeDrafts)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Could not load report',
            // The API explains a rejected range precisely; prefer its wording to a generic one.
            detail: error?.error?.message
              ?? 'Failed to load the project utilization report. Please try again.'
          });
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(result => {
        this.report = result;
        this.loading = false;
        if (this.viewMode === 'chart') {
          this.buildChart();
        }
      });
  }

  onFilterChange(): void {
    this.loadReport();
  }

  // ---------- range validation ----------

  private get startAbs(): number {
    return (this.startYear * 12) + (this.startMonth - 1);
  }

  private get endAbs(): number {
    return (this.endYear * 12) + (this.endMonth - 1);
  }

  /**
   * Checked here as well as on the server so a bad range reads as guidance under the filters
   * rather than as a failed request.
   */
  get rangeError(): string | null {
    if (this.endAbs < this.startAbs) {
      return 'The end month cannot be before the start month.';
    }
    if (this.endAbs - this.startAbs + 1 > this.maxMonths) {
      return `Choose a range of ${this.maxMonths} months or fewer.`;
    }
    return null;
  }

  // ---------- derived views ----------

  /** Projects in view. Matching a member keeps the project, so a name search still finds them. */
  get projects(): ProjectUtilizationProject[] {
    const rows = this.report?.projects ?? [];
    const search = this.searchText.trim().toLowerCase();

    return rows.filter(project => {
      if (search) {
        const matches = project.projectName.toLowerCase().includes(search)
          || (project.clientName ?? '').toLowerCase().includes(search)
          || project.members.some(m => m.employeeName.toLowerCase().includes(search));
        if (!matches) {
          return false;
        }
      }

      if (this.selectedPMs.length && !this.selectedPMs.includes(project.managerName ?? '')) {
        return false;
      }

      if (this.selectedProjectIds.length && !this.selectedProjectIds.includes(project.projectId)) {
        return false;
      }

      if (this.projectTypeFilter === 'presales' && !project.isPresales) {
        return false;
      }
      if (this.projectTypeFilter === 'main' && project.isPresales) {
        return false;
      }

      // The list fields hold every value seen across the period, so a project matches when
      // any one of them is selected rather than needing a single exact value.
      if (this.selectedInvoiceTypes.length
        && !project.invoiceTypes?.some(t => this.selectedInvoiceTypes.includes(t))) {
        return false;
      }

      if (this.selectedCostCenterIds.length
        && !project.costCenterIds?.some(id => this.selectedCostCenterIds.includes(id))) {
        return false;
      }

      if (this.selectedServiceGroupIds.length
        && !project.serviceGroupIds?.some(id => this.selectedServiceGroupIds.includes(id))) {
        return false;
      }

      return true;
    });
  }

  // ---------- filter options ----------
  // Derived from the loaded report, so a dropdown never offers a value with nothing behind it.

  get pmOptions(): { label: string; value: string }[] {
    const names = new Set<string>();
    for (const project of this.report?.projects ?? []) {
      if (project.managerName) {
        names.add(project.managerName);
      }
    }
    return [...names].sort().map(name => ({ label: name, value: name }));
  }

  get projectOptions(): { label: string; value: string }[] {
    return (this.report?.projects ?? [])
      .map(p => ({ label: p.projectName, value: p.projectId }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  get invoiceTypeOptions(): { label: string; value: string }[] {
    const types = new Set<string>();
    for (const project of this.report?.projects ?? []) {
      for (const type of project.invoiceTypes ?? []) {
        types.add(type);
      }
    }
    // "NotAvailable" is the enum's empty case; it reads as a value on screen, so it is
    // labelled the way the allocation grid labels it.
    return [...types].sort().map(type => ({
      label: type === 'NotAvailable' ? 'N/A' : this.splitPascalCase(type),
      value: type
    }));
  }

  get costCenterOptions(): { label: string; value: string }[] {
    return this.pairedOptions(p => p.costCenterIds, p => p.costCenterNames);
  }

  get serviceGroupOptions(): { label: string; value: string }[] {
    return this.pairedOptions(p => p.serviceGroupIds, p => p.serviceGroupNames);
  }

  /**
   * Ids and names arrive as parallel lists on each project. Pairing them by index is safe
   * because the server builds both from the same ordered set.
   */
  private pairedOptions(
    ids: (p: ProjectUtilizationProject) => string[] | undefined,
    names: (p: ProjectUtilizationProject) => string[] | undefined
  ): { label: string; value: string }[] {
    const byId = new Map<string, string>();

    for (const project of this.report?.projects ?? []) {
      const idList = ids(project) ?? [];
      const nameList = names(project) ?? [];
      idList.forEach((id, index) => {
        if (!byId.has(id)) {
          byId.set(id, nameList[index] ?? '(unnamed)');
        }
      });
    }

    return [...byId.entries()]
      .map(([value, label]) => ({ label, value }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  /** "ProjectMilestone" -> "Project Milestone", so enum names read as labels. */
  private splitPascalCase(value: string): string {
    return value.replace(/([a-z])([A-Z])/g, '$1 $2');
  }

  get hasActiveFilters(): boolean {
    return !!this.searchText.trim()
      || this.selectedPMs.length > 0
      || this.selectedProjectIds.length > 0
      || this.projectTypeFilter !== 'all'
      || this.selectedInvoiceTypes.length > 0
      || this.selectedCostCenterIds.length > 0
      || this.selectedServiceGroupIds.length > 0;
  }

  clearFilters(): void {
    this.searchText = '';
    this.selectedPMs = [];
    this.selectedProjectIds = [];
    this.projectTypeFilter = 'all';
    this.selectedInvoiceTypes = [];
    this.selectedCostCenterIds = [];
    this.selectedServiceGroupIds = [];
    // Search lives in the shared header, so clear it there too.
    this.pageHeaderService.setSearchText('');
    this.onProjectFilterChange();
  }

  /**
   * These filters narrow the loaded report rather than refetching it, so the chart just needs
   * redrawing — no request.
   */
  onProjectFilterChange(): void {
    if (this.viewMode === 'chart') {
      this.buildChart();
    }
  }

  // ---------- totals ----------

  get projectCount(): number {
    return this.projects.length;
  }

  get totalAllocatedHours(): number {
    return this.projects.reduce((sum, p) => sum + (p.allocatedHours || 0), 0);
  }

  get totalLoggedHours(): number {
    return this.projects.reduce((sum, p) => sum + (p.loggedHours || 0), 0);
  }

  get totalVarianceHours(): number {
    return this.totalLoggedHours - this.totalAllocatedHours;
  }

  /** Logged as a share of planned across everything in view; null when nothing is planned. */
  get overallUtilization(): number | null {
    const allocated = this.totalAllocatedHours;
    if (allocated <= 0) {
      return null;
    }
    return Math.round((this.totalLoggedHours / allocated) * 1000) / 10;
  }

  /** Distinct people across the projects in view — someone on two projects counts once. */
  get distinctMemberCount(): number {
    const ids = new Set<string>();
    for (const project of this.projects) {
      for (const member of project.members) {
        ids.add(member.employeeId);
      }
    }
    return ids.size;
  }

  // ---------- chart ----------

  setViewMode(mode: 'table' | 'chart'): void {
    this.viewMode = mode;
    if (mode === 'chart') {
      this.buildChart();
    }
  }

  /** True when more projects match than the chart draws, so the page can say so. */
  get chartIsTruncated(): boolean {
    return this.chartTopN > 0 && this.projects.length > this.chartTopN;
  }

  get chartProjectCount(): number {
    if (this.chartTopN === 0) {
      return this.projects.length;
    }
    return Math.min(this.projects.length, this.chartTopN);
  }

  /** The projects the bar chart draws: largest planned effort first, capped by chartTopN. */
  private get chartProjects(): ProjectUtilizationProject[] {
    const sorted = [...this.projects].sort((a, b) => b.allocatedHours - a.allocatedHours);
    return this.chartTopN > 0 ? sorted.slice(0, this.chartTopN) : sorted;
  }

  /**
   * Allocated beside logged, one pair per project. The gap between the two bars is the
   * variance, so it is read directly rather than worked out from two numbers.
   *
   * Chart.js paints to a canvas and cannot resolve CSS variables, so the theme's colours are
   * read off the document here. That also means the chart has to be rebuilt when the theme
   * changes — see the observer in ngOnInit.
   */
  private buildChart(): void {
    const styles = getComputedStyle(document.documentElement);
    const token = (name: string, fallback: string) =>
      styles.getPropertyValue(name).trim() || fallback;

    const textColor = token('--text-color', '#1e293b');
    const mutedColor = token('--text-color-secondary', '#64748b');
    const gridColor = token('--surface-border', '#e2e8f0');

    const top = this.chartProjects;

    this.chartData = {
      labels: top.map(p => p.projectName),
      datasets: [
        {
          label: 'Allocated',
          data: top.map(p => Math.round(p.allocatedHours)),
          backgroundColor: 'rgba(100, 116, 139, 0.55)',
          borderColor: 'rgba(100, 116, 139, 1)',
          borderWidth: 1,
          borderRadius: 3
        },
        {
          label: 'Logged',
          data: top.map(p => Math.round(p.loggedHours)),
          backgroundColor: 'rgba(13, 148, 136, 0.75)',
          borderColor: 'rgba(13, 148, 136, 1)',
          borderWidth: 1,
          borderRadius: 3
        }
      ]
    };

    this.chartOptions = {
      maintainAspectRatio: false,
      // Horizontal bars: project names are long, and vertical labels would be unreadable.
      indexAxis: 'y',
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          position: 'top',
          labels: { usePointStyle: true, color: textColor }
        },
        tooltip: {
          callbacks: {
            label: (item: { dataset: { label?: string }; parsed: { x: number } }) =>
              `  ${item.dataset.label}: ${this.formatHours(item.parsed.x)}`,
            // The variance is the reason to look at this chart, so it is stated outright
            // rather than left to be inferred from the two bars.
            afterBody: (items: { dataIndex: number }[]) => {
              const project = top[items[0]?.dataIndex];
              if (!project) {
                return [];
              }
              const variance = this.formatHours(project.varianceHours);
              const utilization = this.formatPercentage(project.utilizationPercentage);
              return [
                '',
                `Variance: ${variance}`,
                `Utilization: ${utilization}`,
                `Members: ${project.memberCount}`
              ];
            }
          }
        }
      },
      scales: {
        x: {
          beginAtZero: true,
          title: { display: true, text: 'Hours', color: mutedColor },
          ticks: { color: mutedColor },
          grid: { color: gridColor }
        },
        y: {
          ticks: {
            color: textColor,
            // Long project names would otherwise squeeze the plot area to nothing.
            callback: (_value: unknown, index: number) => {
              const name = top[index]?.projectName ?? '';
              return name.length > 28 ? `${name.slice(0, 27)}…` : name;
            }
          },
          grid: { display: false }
        }
      }
    };
  }

  /** Bars need a row each, so the canvas grows with however many are drawn. */
  get chartHeight(): string {
    return `${Math.max(320, this.chartProjectCount * 42 + 80)}px`;
  }

  onChartTopNChange(): void {
    this.buildChart();
  }

  // ---------- summary ----------

  showSummaryDialog = false;

  /** Narrows the summary without disturbing the page's own filters. */
  summarySearch = '';
  summaryMinAllocatedHours = 0;

  readonly summaryMinHoursOptions = [
    { label: 'Any hours', value: 0 },
    { label: '8 h or more', value: 8 },
    { label: '40 h or more', value: 40 },
    { label: '160 h or more', value: 160 }
  ];

  openSummaryDialog(): void {
    this.showSummaryDialog = true;
  }

  closeSummaryDialog(): void {
    this.showSummaryDialog = false;
  }

  /**
   * One row per project — name, allocated, utilized — for the whole selected period.
   *
   * Built from `projects`, so the page's own filters already apply; the controls in the
   * dialog narrow further without touching them. No variance column: the two hour figures
   * sit side by side and the difference is read directly.
   */
  get summaryRows(): SummaryRow[] {
    const search = this.summarySearch.trim().toLowerCase();

    return this.projects
      .filter(project => {
        if (search && !project.projectName.toLowerCase().includes(search)) {
          return false;
        }
        if (project.allocatedHours < this.summaryMinAllocatedHours) {
          return false;
        }
        return true;
      })
      .map(project => ({
        projectId: project.projectId,
        name: project.projectName,
        allocatedHours: project.allocatedHours,
        loggedHours: project.loggedHours,
        utilizationPercentage: project.utilizationPercentage ?? null
      }))
      .sort((a, b) => b.allocatedHours - a.allocatedHours || a.name.localeCompare(b.name));
  }

  /** True while the dialog's own controls are narrowing the list, so a reset is offered. */
  get hasSummaryFilters(): boolean {
    return !!this.summarySearch.trim() || this.summaryMinAllocatedHours > 0;
  }

  get summaryAllocatedTotal(): number {
    return this.summaryRows.reduce((sum, r) => sum + r.allocatedHours, 0);
  }

  get summaryLoggedTotal(): number {
    return this.summaryRows.reduce((sum, r) => sum + r.loggedHours, 0);
  }

  get summaryUtilizationTotal(): number | null {
    const allocated = this.summaryAllocatedTotal;
    if (allocated <= 0) {
      return null;
    }
    return Math.round((this.summaryLoggedTotal / allocated) * 1000) / 10;
  }

  clearSummaryFilters(): void {
    this.summarySearch = '';
    this.summaryMinAllocatedHours = 0;
  }

  exportSummaryToExcel(): void {
    const rows = this.summaryRows;

    if (!rows.length) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Nothing to export',
        detail: 'There is no summary data for the current filters.'
      });
      return;
    }

    try {
      // Mirrors the dialog's columns, with the period named so the sheet stands alone.
      const sheetRows = rows.map(row => ({
        'Project Name': row.name,
        'Period': this.report?.periodLabel ?? '',
        'Allocated Hours': this.round2(row.allocatedHours),
        'Utilized Hours': this.round2(row.loggedHours),
        'Utilization %': row.utilizationPercentage ?? ''
      }));

      const worksheet = XLSX.utils.json_to_sheet(sheetRows);
      autoFitColumns(worksheet, sheetRows);
      addAutoFilter(worksheet);

      const workbook: XLSX.WorkBook = {
        Sheets: { Summary: worksheet },
        SheetNames: ['Summary']
      };
      const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const stamp = new Date().toISOString().slice(0, 10);
      const span = `${this.startYear}-${String(this.startMonth).padStart(2, '0')}`
        + `_to_${this.endYear}-${String(this.endMonth).padStart(2, '0')}`;
      saveAs(blob, `Project_Utilization_Summary_${span}_${stamp}.xlsx`);

      this.messageService.add({
        severity: 'success',
        summary: 'Exported',
        detail: 'The summary has been downloaded.'
      });
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Export failed',
        detail: 'Could not generate the Excel file.'
      });
    }
  }

  // ---------- row expansion ----------

  toggleRow(projectId: string): void {
    this.expandedRows[projectId] = !this.expandedRows[projectId];
  }

  isExpanded(projectId: string): boolean {
    return !!this.expandedRows[projectId];
  }

  // ---------- display helpers ----------

  /** Formats decimal hours as "Xh Ymin" — matches the work log's own presentation. */
  formatHours(decimalHours: number | null | undefined): string {
    if (decimalHours == null || Number.isNaN(decimalHours)) {
      return '—';
    }
    const sign = decimalHours < 0 ? '-' : '';
    const abs = Math.abs(decimalHours);
    const hours = Math.floor(abs);
    const minutes = Math.round((abs - hours) * 60);
    // 59.6 minutes rounds to 60; carry it rather than printing "3h 60min".
    if (minutes === 60) {
      return `${sign}${hours + 1}h 0min`;
    }
    return `${sign}${hours}h ${minutes}min`;
  }

  /** Compact form for the stat cards, where "1,234h" reads better than "1234h 18min". */
  formatHoursCompact(decimalHours: number | null | undefined): string {
    if (decimalHours == null || Number.isNaN(decimalHours)) {
      return '—';
    }
    return `${Math.round(decimalHours).toLocaleString()} h`;
  }

  formatVarianceCompact(decimalHours: number | null | undefined): string {
    if (decimalHours == null || Number.isNaN(decimalHours)) {
      return '—';
    }
    const rounded = Math.round(decimalHours);
    const sign = rounded > 0 ? '+' : rounded < 0 ? '−' : '';
    return `${sign}${Math.abs(rounded).toLocaleString()} h`;
  }

  formatPercentage(value: number | null | undefined): string {
    if (value == null || Number.isNaN(value)) {
      return '—';
    }
    return `${value.toFixed(1)}%`;
  }

  /**
   * Utilization colour. Over 100% is not a breach here the way over-allocation is — it means
   * more was logged than planned, which is worth noticing rather than flagging red.
   */
  utilizationClass(value: number | null | undefined): string {
    if (value == null) {
      return 'pur-util-none';
    }
    if (value > 110) {
      return 'pur-util-over';
    }
    if (value < 90) {
      return 'pur-util-under';
    }
    return 'pur-util-on-track';
  }

  // ---------- export ----------

  exportToExcel(): void {
    if (!this.report || !this.projects.length) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Nothing to export',
        detail: 'There is no report data for the selected period.'
      });
      return;
    }

    try {
      // One row per project/member, with the project total on its own row, so the sheet
      // stands alone without the expand interaction.
      const rows: Record<string, unknown>[] = [];

      for (const project of this.projects) {
        rows.push({
          'Project': project.projectName,
          'Client': project.clientName ?? '',
          'Project Manager': project.managerName ?? '',
          'Member': '— Project total —',
          'Role': '',
          'Months Allocated': '',
          'Allocated Hours': this.round2(project.allocatedHours),
          'Logged Hours': this.round2(project.loggedHours),
          'Variance Hours': this.round2(project.varianceHours),
          'Utilization %': project.utilizationPercentage ?? ''
        });

        for (const member of project.members) {
          rows.push({
            'Project': project.projectName,
            'Client': project.clientName ?? '',
            'Project Manager': project.managerName ?? '',
            'Member': member.employeeName,
            'Role': member.roleTitle ?? '',
            'Months Allocated': member.monthsAllocated,
            'Allocated Hours': this.round2(member.allocatedHours),
            'Logged Hours': this.round2(member.loggedHours),
            'Variance Hours': this.round2(member.varianceHours),
            'Utilization %': member.utilizationPercentage ?? ''
          });
        }
      }

      const worksheet = XLSX.utils.json_to_sheet(rows);
      autoFitColumns(worksheet, rows);
      addAutoFilter(worksheet);

      const sheets: Record<string, XLSX.WorkSheet> = { 'Project Utilization': worksheet };
      const workbook: XLSX.WorkBook = { Sheets: sheets, SheetNames: ['Project Utilization'] };
      const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const stamp = new Date().toISOString().slice(0, 10);
      const span = `${this.startYear}-${String(this.startMonth).padStart(2, '0')}`
        + `_to_${this.endYear}-${String(this.endMonth).padStart(2, '0')}`;
      saveAs(blob, `Project_Utilization_${span}_${stamp}.xlsx`);

      this.messageService.add({
        severity: 'success',
        summary: 'Exported',
        detail: 'The project utilization report has been downloaded.'
      });
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Export failed',
        detail: 'Could not generate the Excel file.'
      });
    }
  }

  private round2(value: number | null | undefined): number {
    if (value == null || Number.isNaN(value)) {
      return 0;
    }
    return Math.round(value * 100) / 100;
  }
}
